import axios from "axios";
import { DEFAULT_AXIOS_CONFIG } from "./userService";
import { resolveGatewayBackedServiceBaseUrl } from "./serviceBaseUrl";

const MEDIA_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_MEDIA_SERVICE_URL);
const PUBLIC_AUDIENCE = "public";

const withAuth = (config = {}) => ({ ...DEFAULT_AXIOS_CONFIG, ...config });
const isPublicAudience = (options = {}) => options?.audience === PUBLIC_AUDIENCE;
const withMediaAudience = (options = {}, config = {}) => (
  isPublicAudience(options) ? { ...config, withCredentials: false } : withAuth(config)
);

async function uploadScopedImage(path, file) {
  if (!path) throw new Error("path is required");
  if (!file) throw new Error("file is required");
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}${path}`,
    formData,
    {
      ...withAuth(),
      headers: {
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

export async function uploadChatAttachment(chatId, file) {
  if (!chatId) throw new Error("chatId is required");
  if (!file) throw new Error("file is required");
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}/api/media/chat/${encodeURIComponent(chatId)}/attachments`,
    formData,
    {
      ...withAuth(),
      headers: {
        // Let Axios compute multipart boundaries automatically; only signal that we expect JSON back.
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

export async function uploadPostAttachment(postId, file) {
  if (!postId) throw new Error("postId is required");
  if (!file) throw new Error("file is required");
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}/api/media/posts/${encodeURIComponent(postId)}/attachments`,
    formData,
    {
      ...withAuth(),
      headers: {
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

export async function uploadUserMediaPhoto(file) {
  if (!file) throw new Error("file is required");
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}/api/media/user-photos`,
    formData,
    {
      ...withAuth(),
      headers: {
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

export async function listUserMediaPhotos(userId, options = {}) {
  if (!userId) throw new Error("userId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/user-photos"
    : "/api/media/user-photos";
  const response = await axios.get(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(userId)}`,
    withMediaAudience(options)
  );
  return response.data;
}

export async function uploadBandMediaPhoto(bandId, file) {
  if (!bandId) throw new Error("bandId is required");
  if (!file) throw new Error("file is required");
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}/api/media/bands/${encodeURIComponent(bandId)}/photos`,
    formData,
    {
      ...withAuth(),
      headers: {
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

export async function listBandMediaPhotos(bandId, options = {}) {
  if (!bandId) throw new Error("bandId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/bands"
    : "/api/media/bands";
  const response = await axios.get(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(bandId)}/photos`,
    withMediaAudience(options)
  );
  return response.data;
}

export async function reorderBandMediaPhotos(bandId, objectKeys) {
  if (!bandId) throw new Error("bandId is required");
  if (!Array.isArray(objectKeys) || objectKeys.length === 0) {
    throw new Error("objectKeys are required");
  }
  const response = await axios.put(
    `${MEDIA_SERVICE_URL}/api/media/bands/${encodeURIComponent(bandId)}/photos/order`,
    { objectKeys },
    withAuth()
  );
  return response.data;
}

export async function deleteBandMediaPhoto(bandId, objectKey) {
  if (!bandId) throw new Error("bandId is required");
  if (!objectKey) throw new Error("objectKey is required");
  await axios.delete(
    `${MEDIA_SERVICE_URL}/api/media/bands/${encodeURIComponent(bandId)}/photos`,
    withAuth({ params: { objectKey } })
  );
}

export async function reorderUserMediaPhotos(objectKeys) {
  if (!Array.isArray(objectKeys) || objectKeys.length === 0) {
    throw new Error("objectKeys are required");
  }
  const response = await axios.put(
    `${MEDIA_SERVICE_URL}/api/media/user-photos/order`,
    { objectKeys },
    withAuth()
  );
  return response.data;
}

export async function deleteUserMediaPhoto(objectKey) {
  if (!objectKey) throw new Error("objectKey is required");
  await axios.delete(
    `${MEDIA_SERVICE_URL}/api/media/user-photos`,
    withAuth({ params: { objectKey } })
  );
}

async function uploadMediaTrack(path, file, options = {}) {
  if (!path) throw new Error("path is required");
  if (!file) throw new Error("file is required");
  const formData = new FormData();
  formData.append("file", file);
  if (options.title) {
    formData.append("title", options.title);
  }
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}${path}`,
    formData,
    {
      ...withAuth(),
      headers: {
        Accept: "application/json",
      },
    }
  );
  return response.data;
}

export async function uploadProfileTrack(file, options = {}) {
  return uploadMediaTrack("/api/media/profile-tracks", file, options);
}

export async function listProfileTracks(userId, options = {}) {
  if (!userId) throw new Error("userId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/profile-tracks"
    : "/api/media/profile-tracks";
  const response = await axios.get(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(userId)}`,
    withMediaAudience(options)
  );
  return response.data;
}

export async function reorderProfileTracks(objectKeys) {
  if (!Array.isArray(objectKeys) || objectKeys.length === 0) {
    throw new Error("objectKeys are required");
  }
  const response = await axios.put(
    `${MEDIA_SERVICE_URL}/api/media/profile-tracks/order`,
    { objectKeys },
    withAuth()
  );
  return response.data;
}

export async function uploadBandTrack(bandId, file, options = {}) {
  if (!bandId) throw new Error("bandId is required");
  return uploadMediaTrack(`/api/media/bands/${encodeURIComponent(bandId)}/tracks`, file, options);
}

export async function listBandTracks(bandId, options = {}) {
  if (!bandId) throw new Error("bandId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/bands"
    : "/api/media/bands";
  const response = await axios.get(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(bandId)}/tracks`,
    withMediaAudience(options)
  );
  return response.data;
}

export async function getBandPlaysCount(bandId, options = {}) {
  if (!bandId) throw new Error("bandId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/bands"
    : "/api/media/bands";
  const response = await axios.get(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(bandId)}/tracks/plays-count`,
    withMediaAudience(options)
  );
  return response.data;
}

export async function reorderBandTracks(bandId, objectKeys) {
  if (!bandId) throw new Error("bandId is required");
  if (!Array.isArray(objectKeys) || objectKeys.length === 0) {
    throw new Error("objectKeys are required");
  }
  const response = await axios.put(
    `${MEDIA_SERVICE_URL}/api/media/bands/${encodeURIComponent(bandId)}/tracks/order`,
    { objectKeys },
    withAuth()
  );
  return response.data;
}

export async function createProfileTrackPlaySession(userId, objectKey, options = {}) {
  if (!userId) throw new Error("userId is required");
  if (!objectKey) throw new Error("objectKey is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/profile-tracks"
    : "/api/media/profile-tracks";
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(userId)}/play-sessions`,
    null,
    withMediaAudience(options, { params: { objectKey } })
  );
  return response.data;
}

export async function createBandTrackPlaySession(bandId, objectKey, options = {}) {
  if (!bandId) throw new Error("bandId is required");
  if (!objectKey) throw new Error("objectKey is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/bands"
    : "/api/media/bands";
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(bandId)}/tracks/play-sessions`,
    null,
    withMediaAudience(options, { params: { objectKey } })
  );
  return response.data;
}

export async function recordProfileTrackPlay(userId, objectKey, playSessionId, options = {}) {
  if (!userId) throw new Error("userId is required");
  if (!objectKey) throw new Error("objectKey is required");
  if (!playSessionId) throw new Error("playSessionId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/profile-tracks"
    : "/api/media/profile-tracks";
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(userId)}/plays`,
    null,
    withMediaAudience(options, { params: { objectKey, playSessionId } })
  );
  return response.data;
}

export async function recordBandTrackPlay(bandId, objectKey, playSessionId, options = {}) {
  if (!bandId) throw new Error("bandId is required");
  if (!objectKey) throw new Error("objectKey is required");
  if (!playSessionId) throw new Error("playSessionId is required");
  const basePath = isPublicAudience(options)
    ? "/api/media/public/bands"
    : "/api/media/bands";
  const response = await axios.post(
    `${MEDIA_SERVICE_URL}${basePath}/${encodeURIComponent(bandId)}/tracks/plays`,
    null,
    withMediaAudience(options, { params: { objectKey, playSessionId } })
  );
  return response.data;
}

export async function deleteProfileTrack(objectKey) {
  if (!objectKey) throw new Error("objectKey is required");
  await axios.delete(
    `${MEDIA_SERVICE_URL}/api/media/profile-tracks`,
    withAuth({ params: { objectKey } })
  );
}

export async function deleteBandTrack(bandId, objectKey) {
  if (!bandId) throw new Error("bandId is required");
  if (!objectKey) throw new Error("objectKey is required");
  await axios.delete(
    `${MEDIA_SERVICE_URL}/api/media/bands/${encodeURIComponent(bandId)}/tracks`,
    withAuth({ params: { objectKey } })
  );
}

export async function uploadBandImage(bandId, file) {
  if (!bandId) throw new Error("bandId is required");
  return uploadScopedImage(`/api/media/bands/${encodeURIComponent(bandId)}/image`, file);
}

export async function uploadGroupImage(groupId, file) {
  if (!groupId) throw new Error("groupId is required");
  return uploadScopedImage(`/api/media/groups/${encodeURIComponent(groupId)}/image`, file);
}

const mediaService = {
  uploadChatAttachment,
  uploadPostAttachment,
  uploadUserMediaPhoto,
  listUserMediaPhotos,
  uploadBandMediaPhoto,
  listBandMediaPhotos,
  reorderBandMediaPhotos,
  deleteBandMediaPhoto,
  reorderUserMediaPhotos,
  deleteUserMediaPhoto,
  uploadProfileTrack,
  listProfileTracks,
  reorderProfileTracks,
  createProfileTrackPlaySession,
  recordProfileTrackPlay,
  deleteProfileTrack,
  uploadBandTrack,
  listBandTracks,
  getBandPlaysCount,
  reorderBandTracks,
  createBandTrackPlaySession,
  recordBandTrackPlay,
  deleteBandTrack,
  uploadBandImage,
  uploadGroupImage,
};
export default mediaService;
